/**
 * File Upload Utility
 * Dosya yükleme, validasyon ve format dönüştürme işlemleri
 */

// Dosya boyutu limitleri (bytes)
export const FILE_SIZE_LIMITS = {
  IMAGE: 5 * 1024 * 1024,      // 5MB
  PDF: 10 * 1024 * 1024,       // 10MB
  DOCUMENT: 10 * 1024 * 1024,  // 10MB
};

// İzin verilen dosya tipleri
export const ALLOWED_FILE_TYPES = {
  IMAGE: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  PDF: ['application/pdf'],
  DOCUMENT: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
};

/**
 * Dosya boyutunu kontrol eder
 */
export const validateFileSize = (file, maxSize) => {
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    throw new Error(`Dosya boyutu ${maxSizeMB}MB'dan küçük olmalıdır`);
  }
  return true;
};

/**
 * Dosya tipini kontrol eder
 */
export const validateFileType = (file, allowedTypes) => {
  // Eğer allowedTypes array değilse, string olarak geliyorsa parse et
  let types = allowedTypes;
  if (typeof allowedTypes === 'string') {
    // "image/*,application/pdf" gibi string'i array'e çevir
    types = allowedTypes.split(',').map(t => t.trim());
  }
  
  // Wildcard desteği (image/*, video/* gibi)
  const isAllowed = types.some(type => {
    if (type.endsWith('/*')) {
      const baseType = type.split('/')[0];
      return file.type.startsWith(baseType + '/');
    }
    return file.type === type;
  });
  
  if (!isAllowed) {
    throw new Error(`Geçersiz dosya formatı. İzin verilen: ${types.join(', ')}`);
  }
  return true;
};

/**
 * Resmi WEBP formatına dönüştürür
 */
export const convertImageToWebP = (file, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    // PDF veya diğer dosyalar için dönüştürme yapma
    if (file.type === 'application/pdf' || !file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const webpFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, '.webp'),
                { type: 'image/webp' }
              );
              resolve(webpFile);
            } else {
              reject(new Error('WEBP dönüştürme başarısız'));
            }
          },
          'image/webp',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Resim yüklenemedi'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Dosya okunamadı'));
    reader.readAsDataURL(file);
  });
};

/**
 * Resmi yeniden boyutlandırır (max width/height)
 */
export const resizeImage = (file, maxWidth = 1920, maxHeight = 1080) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;
        
        // Oranı koru
        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = width * ratio;
          height = height * ratio;
        }
        
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const resizedFile = new File([blob], file.name, { type: file.type });
              resolve(resizedFile);
            } else {
              reject(new Error('Boyutlandırma başarısız'));
            }
          },
          file.type,
          0.9
        );
      };
      
      img.onerror = () => reject(new Error('Resim yüklenemedi'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Dosya okunamadı'));
    reader.readAsDataURL(file);
  });
};

/**
 * Dosyayı tam validasyon ile hazırlar
 */
export const prepareFileForUpload = async (file, options = {}) => {
  const {
    maxSize = FILE_SIZE_LIMITS.IMAGE,
    allowedTypes = ALLOWED_FILE_TYPES.IMAGE,
    convertToWebP = true,
    resize = true,
    maxWidth = 1920,
    maxHeight = 1080,
  } = options;

  try {
    // Validasyon
    validateFileType(file, allowedTypes);
    validateFileSize(file, maxSize);

    let processedFile = file;

    // Sadece resimler için işlem yap
    if (file.type.startsWith('image/')) {
      // Boyutlandır
      if (resize) {
        processedFile = await resizeImage(processedFile, maxWidth, maxHeight);
      }

      // WEBP'ye dönüştür
      if (convertToWebP) {
        processedFile = await convertImageToWebP(processedFile);
      }
    }

    return processedFile;
  } catch (error) {
    throw error;
  }
};

/**
 * Dosya önizlemesi oluşturur
 */
export const createFilePreview = (file) => {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Önizleme oluşturulamadı'));
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      // PDF için icon göster
      resolve('pdf');
    } else {
      resolve('document');
    }
  });
};

/**
 * Dosya boyutunu okunabilir formata çevirir
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * FormData'ya dosya ekler
 */
export const appendFileToFormData = (formData, fieldName, file) => {
  formData.append(fieldName, file);
  return formData;
};

/**
 * Çoklu dosya yükleme
 */
export const prepareMultipleFiles = async (files, options = {}) => {
  const promises = Array.from(files).map(file => 
    prepareFileForUpload(file, options)
  );
  
  return Promise.all(promises);
};
